import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import PygAnaliticoPageHeader, {
  pygToolbarControlSx,
  pygToolbarFieldSx,
} from "../PyGAnalitico/PygAnaliticoPageHeader";
import {
  PygExportMenu,
  PygToolbarFilters,
  PygToolbarPanel,
} from "../PyGAnalitico/PygAnaliticoToolbar";
import { pageContentCardSx } from "../../styles/pageModernStyles";
import { getUserById } from "../../api/User/userApi";
import { mapUserToPermissionState } from "../../utils/userPermissionsUtils";
import { pygAnaliticoBasePath } from "../../constants/pygEmpresas";
import { NML_BALANCE_PATH } from "../../constants/balanceNpgcEstructura";
import { PYG_AVAILABLE_YEARS } from "../PyGAnalitico/pygChartCompareYears";
import {
  pygExcelI18nOpts,
  translatePygLine,
} from "../../constants/pygLineI18n";
import { authFetch } from "../../api/authFetch";
import {
  balanceDisplayTitle,
  balanceMonthStatus,
  buildBalanceAnaliticoStructure,
  buildExcelRows,
  defaultBalanceThroughMonth,
  monthsForBalanceView,
  parseBalanceNpgcResponse,
} from "./balanceAnaliticoBuild";

const API_URL = process.env.REACT_APP_API_URL;
const COLOR_VALOR_POSITIVO = "#2E7D32";
const COLOR_VALOR_NEGATIVO = "#FF2800";
const COLOR_VALOR_CERO = "rgba(92, 114, 117, 0.55)";

const colorPorSigno = (valor) => {
  if (valor === 0 || valor === null || valor === undefined)
    return COLOR_VALOR_CERO;
  return Number(valor) >= 0 ? COLOR_VALOR_POSITIVO : COLOR_VALOR_NEGATIVO;
};

const formatearNumero = (valor) =>
  (valor ?? 0).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });

const NmlBalanceTable = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const accent = theme.palette.primary.main;
  const pageGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : "linear-gradient(180deg, #f8fafc 0%, #eef6f8 45%, #f5f3ff 100%)";

  const [year, setYear] = useState(new Date().getFullYear());
  const [throughMonth, setThroughMonth] = useState(() =>
    defaultBalanceThroughMonth(new Date().getFullYear()),
  );
  const [rawData, setRawData] = useState([]);
  const [fechaCierre, setFechaCierre] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gruposAbiertos, setGruposAbiertos] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");
    let cancelled = false;
    (async () => {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp < Date.now() / 1000) {
          localStorage.removeItem("token");
          navigate("/login");
          return;
        }
        const user = await getUserById(decoded.userId);
        if (cancelled) return;
        const perms = mapUserToPermissionState(user);
        if (!perms.Permiso_NMLFinanzas) {
          navigate("/", { replace: true });
        }
      } catch {
        if (!cancelled) navigate("/login");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const url = `${API_URL}/api/pyg/saldos-balance-npgc?year=${year}&empresa=nml_spain`;
    authFetch(url, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((data) => {
        const parsed = parseBalanceNpgcResponse(data);
        setRawData(parsed.rows);
        setFechaCierre(parsed.fechaCierre);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [year]);

  const mesesPeriodo = useMemo(() => monthsForBalanceView(year), [year]);
  const monthLabel = t(`pygAnalitico.balance.months.${throughMonth}`);
  const statusOf = (mes) => balanceMonthStatus(year, Number(mes), fechaCierre);
  const statusLabel = (status) =>
    status === "closed"
      ? t("pygAnalitico.balance.statusClosed")
      : status === "open"
        ? t("pygAnalitico.balance.statusOpen")
        : "";
  const asOfStatus = statusOf(throughMonth);
  const fechaCierreLabel = fechaCierre
    ? new Date(`${fechaCierre}T00:00:00`).toLocaleDateString(i18n.language, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";
  const asOfColSx = (mes, extra = {}) =>
    Number(mes) === throughMonth
      ? {
          ...extra,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          fontWeight: 800,
        }
      : extra;

  const { groupOrder, groupMap, totalActivo, totalPnPasivo, cuadre } = useMemo(
    () => buildBalanceAnaliticoStructure(rawData, undefined, { throughMonth }),
    [rawData, throughMonth],
  );

  const toggleGrupo = (codigo) => {
    setGruposAbiertos((prev) => ({ ...prev, [codigo]: !prev[codigo] }));
  };

  const exportToExcel = () => {
    const excelOrder = [];
    const excelMap = {};
    groupOrder.forEach((codigo) => {
      const g = groupMap[codigo];
      if (!g) return;
      const title = `${g.codigo} · ${g.titulo}`;
      excelOrder.push(title);
      excelMap[title] = { ...g, titulo: title };
    });
    const { headers, rows } = buildExcelRows(
      excelOrder,
      excelMap,
      mesesPeriodo,
      pygExcelI18nOpts(t),
    );
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Balance");
    XLSX.writeFile(
      wb,
      `balance-nml-spain-${year}-m${String(throughMonth).padStart(2, "0")}.xlsx`,
    );
  };

  const gantBg = theme.palette.gant?.main ?? theme.palette.gant ?? "#e0e0e0";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        maxHeight: "100dvh",
        boxSizing: "border-box",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        background: pageGradient,
        pt: { xs: 1, md: 1.25 },
        px: { xs: 1.25, sm: 2 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 1.5 },
        ...contentMargin,
        minWidth: 0,
        width: "100%",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <PygAnaliticoPageHeader
          sticky={!isMobile}
          compact
          eyebrow={t("pygAnalitico.balance.eyebrow")}
          title={t("pygAnalitico.balance.title", { year })}
          subtitle={t("pygAnalitico.balance.subtitle")}
          filtroResumen={t("pygAnalitico.balance.filtroPeriod", {
            month: monthLabel,
            year,
          })}
          kpiChips={
            <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                icon={<AutoGraphOutlinedIcon sx={{ fontSize: 16 }} />}
                label={
                  asOfStatus === "unknown"
                    ? t("pygAnalitico.balance.periodChip", {
                        month: monthLabel,
                        year,
                      })
                    : `${monthLabel} ${year} · ${statusLabel(asOfStatus)}`
                }
                sx={{
                  fontWeight: 700,
                  bgcolor: theme.palette.background.paper,
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                }}
              />
              {fechaCierreLabel ? (
                <Chip
                  size="small"
                  icon={<LockOutlinedIcon sx={{ fontSize: 16 }} />}
                  label={t("pygAnalitico.balance.closedUntil", {
                    date: fechaCierreLabel,
                  })}
                  sx={{ fontWeight: 700 }}
                />
              ) : null}
              <Chip
                size="small"
                label={`${t("pygAnalitico.balance.totalActivo")}: ${formatearNumero(totalActivo)}`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                label={`${t("pygAnalitico.balance.totalPnPasivo")}: ${formatearNumero(totalPnPasivo)}`}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                label={`${t("pygAnalitico.balance.cuadre")}: ${formatearNumero(cuadre)}`}
                sx={{
                  fontWeight: 800,
                  color:
                    Math.abs(cuadre) < 0.05
                      ? COLOR_VALOR_POSITIVO
                      : COLOR_VALOR_NEGATIVO,
                }}
              />
            </Stack>
          }
          navChips={
            <>
              <Chip
                size="small"
                icon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                label={t("pygAnalitico.balance.navPyg")}
                variant="outlined"
                clickable
                onClick={() => navigate(pygAnaliticoBasePath("nml_spain"))}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 16 }} />}
                label={t("pygAnalitico.balance.navBalance")}
                color="primary"
                sx={{ fontWeight: 700 }}
              />
            </>
          }
          toolbar={
            <PygToolbarPanel variant="sidebar">
              <PygToolbarFilters variant="sidebar">
                <FormControl size="small" fullWidth sx={pygToolbarControlSx}>
                  <InputLabel>{t("pygAnalitico.year")}</InputLabel>
                  <Select
                    value={year}
                    label={t("pygAnalitico.year")}
                    onChange={(e) => {
                      const nextYear = Number(e.target.value);
                      setYear(nextYear);
                      setThroughMonth(defaultBalanceThroughMonth(nextYear));
                    }}
                    sx={pygToolbarFieldSx(theme)}
                  >
                    {[...PYG_AVAILABLE_YEARS].map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth sx={pygToolbarControlSx}>
                  <InputLabel>{t("pygAnalitico.balance.until")}</InputLabel>
                  <Select
                    value={throughMonth}
                    label={t("pygAnalitico.balance.until")}
                    onChange={(e) => setThroughMonth(Number(e.target.value))}
                    sx={pygToolbarFieldSx(theme)}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                      const st = statusOf(m);
                      const extra = statusLabel(st);
                      return (
                        <MenuItem key={m} value={m}>
                          {extra
                            ? `${t(`pygAnalitico.balance.months.${m}`)} · ${extra}`
                            : t(`pygAnalitico.balance.months.${m}`)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </PygToolbarFilters>
              <PygExportMenu
                items={[
                  {
                    key: "vista",
                    label: t("pygAnalitico.exportCurrentView"),
                    onClick: exportToExcel,
                  },
                ]}
              />
            </PygToolbarPanel>
          }
        />
      </Box>

      {error ? (
        <Typography color="error" sx={{ mb: 1, flexShrink: 0 }}>
          {error}
        </Typography>
      ) : null}

      {loading ? (
        <Box sx={{ p: 0.5 }}>
          <Skeleton variant="rounded" sx={{ minHeight: 280 }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            ...pageContentCardSx(theme, accent),
            p: 0,
          }}
        >
          <Table
            size="small"
            sx={{
              "& .MuiTableCell-root": {
                py: 0.65,
                borderBottomColor: alpha(theme.palette.divider, 0.6),
              },
            }}
          >
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 2,
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: theme.palette.background.paper,
                    zIndex: 1,
                  }}
                >
                  <strong>{t("pygAnalitico.titleCol")}</strong>
                </TableCell>
                {mesesPeriodo.map((mes) => {
                  const st = statusOf(mes);
                  const closed = st === "closed";
                  const open = st === "open";
                  const hint = closed
                    ? t("pygAnalitico.balance.monthClosedHint")
                    : open
                      ? t("pygAnalitico.balance.monthOpenHint")
                      : "";
                  const Icon = closed
                    ? LockOutlinedIcon
                    : open
                      ? LockOpenOutlinedIcon
                      : null;
                  const header = (
                    <Stack
                      direction="row"
                      spacing={0.4}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      {Icon ? (
                        <Icon
                          sx={{
                            fontSize: 14,
                            color: closed ? "text.secondary" : "warning.main",
                          }}
                        />
                      ) : null}
                      <strong>{t(`pygAnalitico.balance.months.${mes}`)}</strong>
                    </Stack>
                  );
                  return (
                    <TableCell
                      key={mes}
                      align="right"
                      sx={asOfColSx(mes, {
                        color: closed
                          ? "text.secondary"
                          : open
                            ? "warning.dark"
                            : undefined,
                      })}
                    >
                      {hint ? (
                        <Tooltip title={hint}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              cursor: "help",
                            }}
                          >
                            {header}
                          </Box>
                        </Tooltip>
                      ) : (
                        header
                      )}
                    </TableCell>
                  );
                })}
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    fontWeight: 800,
                  }}
                >
                  <strong>
                    {asOfStatus === "unknown"
                      ? t("pygAnalitico.total")
                      : `${t("pygAnalitico.total")} · ${statusLabel(asOfStatus)}`}
                  </strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupOrder.map((codigo) => {
                const g = groupMap[codigo];
                if (!g) return null;
                const isCalculated = g._calculated;
                const open = isCalculated ? false : gruposAbiertos[codigo];
                const rowBg = isCalculated
                  ? (theme.palette.pyg1?.main ??
                    theme.palette.pyg1 ??
                    "#d4e6e0")
                  : g.isSection
                    ? alpha(theme.palette.primary.main, 0.08)
                    : gantBg;
                const title = translatePygLine(t, balanceDisplayTitle(g));
                return (
                  <React.Fragment key={codigo}>
                    <TableRow
                      sx={{
                        backgroundColor: rowBg,
                        cursor: isCalculated ? "default" : "pointer",
                      }}
                      onClick={() => !isCalculated && toggleGrupo(codigo)}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: rowBg,
                          fontWeight: g.isSection || isCalculated ? 800 : 600,
                          fontSize: "12px",
                          whiteSpace: "pre",
                        }}
                      >
                        {isCalculated ? "" : open ? "▼ " : "► "}
                        {title}
                      </TableCell>
                      {mesesPeriodo.map((mes) => {
                        const numMes = parseInt(mes, 10);
                        const val = g.mes[numMes] ?? 0;
                        return (
                          <TableCell
                            key={mes}
                            align="right"
                            sx={asOfColSx(mes, {
                              color: colorPorSigno(val),
                              fontSize: "12px",
                            })}
                          >
                            {formatearNumero(val)}
                          </TableCell>
                        );
                      })}
                      <TableCell
                        align="right"
                        sx={{
                          color: colorPorSigno(g.total),
                          fontWeight: 700,
                          fontSize: "12px",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }}
                      >
                        {formatearNumero(g.total)}
                      </TableCell>
                    </TableRow>
                    {open &&
                      (g.subGroups.__root__?.accountList || []).map((acc) => (
                        <TableRow key={`${codigo}-${acc.codigo}`}>
                          <TableCell
                            sx={{
                              paddingLeft: "2.5em",
                              position: "sticky",
                              left: 0,
                              backgroundColor: theme.palette.background.paper,
                              fontSize: "12px",
                            }}
                          >
                            {acc.codigo} · {acc.titulo}
                          </TableCell>
                          {mesesPeriodo.map((mes) => {
                            const numMes = parseInt(mes, 10);
                            const val = acc.mes[numMes] ?? 0;
                            return (
                              <TableCell
                                key={mes}
                                align="right"
                                sx={asOfColSx(mes, {
                                  color: colorPorSigno(val),
                                  fontSize: "12px",
                                })}
                              >
                                {formatearNumero(val)}
                              </TableCell>
                            );
                          })}
                          <TableCell
                            align="right"
                            sx={{
                              color: colorPorSigno(acc.total),
                              fontSize: "12px",
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                            }}
                          >
                            {formatearNumero(acc.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default NmlBalanceTable;
export { NML_BALANCE_PATH };
